import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/planner/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Settings() {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    api.get("/settings").then(({ data }) => setSettings(data)).catch(() => toast.error("Could not load settings"));
  }, []);

  const save = async () => {
    try {
      const { data } = await api.put("/settings", {
        target_margin_pct: Number(settings.target_margin_pct) || null,
        markup_low_pct: settings.markup_low_pct === "" || settings.markup_low_pct == null ? null : Number(settings.markup_low_pct),
        markup_tier_cost: settings.markup_tier_cost === "" || settings.markup_tier_cost == null ? null : Number(settings.markup_tier_cost),
        min_vend_price: settings.min_vend_price === "" || settings.min_vend_price == null ? null : Number(settings.min_vend_price),
        target_cost_default: settings.target_cost_default === "" || settings.target_cost_default == null ? null : Number(settings.target_cost_default),
      });
      setSettings(data);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  if (!settings) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div>
      <div className="mb-5">
        <h1 className="font-heading text-2xl font-bold">Settings</h1>
        <p className="text-sm text-muted-foreground">Defaults used for pricing and fill planning.</p>
      </div>
      <Card className="max-w-lg space-y-4 p-5">
        <div>
          <Label>Markup</Label>
          <p className="mb-2 text-xs text-muted-foreground">Added on top of cost, then rounded up to the next quarter. Items that cost up to the tier get the low-cost markup, dearer ones the regular markup, and never sell for less than an item at the tier. Set the tier to 0 for one markup on everything.</p>
          <div className="grid grid-cols-3 gap-3">
            <div><Label className="text-xs">Low-cost items (%)</Label>
              <Input data-testid="settings-low-margin-input" type="number" min="0" max="500" value={settings.markup_low_pct ?? ""}
                onChange={(e) => setSettings({ ...settings, markup_low_pct: e.target.value })} /></div>
            <div><Label className="text-xs">Tier: cost up to ($)</Label>
              <Input data-testid="settings-tier-input" type="number" min="0" step="0.05" value={settings.markup_tier_cost ?? ""}
                onChange={(e) => setSettings({ ...settings, markup_tier_cost: e.target.value })} /></div>
            <div><Label className="text-xs">Regular (%)</Label>
              <Input data-testid="settings-margin-input" type="number" min="0" max="500" value={settings.target_margin_pct ?? ""}
                onChange={(e) => setSettings({ ...settings, target_margin_pct: e.target.value })} /></div>
          </div>
          <div className="mt-3 w-40"><Label className="text-xs">Minimum price ($)</Label>
            <Input data-testid="settings-min-price-input" type="number" min="0" step="0.25" value={settings.min_vend_price ?? ""}
              onChange={(e) => setSettings({ ...settings, min_vend_price: e.target.value })} /></div>
        </div>
        <div>
          <Label>Default target cost per item ($)</Label>
          <Input data-testid="settings-target-input" type="number" step="0.01" value={settings.target_cost_default ?? ""}
            onChange={(e) => setSettings({ ...settings, target_cost_default: e.target.value })} />
        </div>
        <Button data-testid="settings-save-button" onClick={save}>Save settings</Button>
      </Card>
    </div>
  );
}
